export const RALLY_STATUS_OPTIONS = ["upcoming", "active", "completed"];

const emptyDocument = () => ({
  documentUrl: "",
  title: "",
  bgImage: "",
});

const emptyDestination = () => ({
  title: "",
  image: "",
});

export const initialRallyForm = {
  name: "",
  edition_number: "",
  route_id: "",
  types: [],
  categories: [],
  pricing: {},
  date: "",
  end_date: "",
  registration_start_date: "",
  registration_end_date: "",
  location: "",
  organiser: "",
  description: "",
  status: "upcoming",
  highlights: [""],
  stats_drivers: "0",
  stats_events: "0",
  stats_participants: "0",
  coverImage: "",
  buttonText: ["", ""],
  imagesGallery: [""],
  videoGallery: [""],
  showContentAsGlobal: false,
  documents: [emptyDocument()],
  destinations: [emptyDestination()],
  track: { title: "", description: "", image: "" },
};

const toDateInput = (dateValue) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const asStringArray = (value, fallback = [""]) => {
  if (!Array.isArray(value) || !value.length) return fallback;
  return value.map((item) => String(item ?? ""));
};

const mapDocumentsToForm = (documents) => {
  if (!Array.isArray(documents) || !documents.length) {
    return [emptyDocument()];
  }

  return documents.map((doc) => ({
    documentUrl: String(
      doc?.documentUrl ?? doc?.document_url ?? doc?.url ?? ""
    ),
    title: String(doc?.title ?? ""),
    bgImage: String(doc?.bgImage ?? doc?.bg_image ?? ""),
  }));
};

const mapDestinationsToForm = (destinations) => {
  if (!Array.isArray(destinations) || !destinations.length) {
    return [emptyDestination()];
  }

  return destinations.map((item) => ({
    title: String(item?.title ?? ""),
    image: String(item?.image ?? ""),
  }));
};

const mapTrackToForm = (track) => {
  if (!track || typeof track !== "object") {
    return { title: "", description: "", image: "" };
  }

  return {
    title: String(track.title ?? ""),
    description: String(track.description ?? ""),
    image: String(track.image ?? ""),
  };
};

const mapButtonTextToForm = (buttonText) => {
  const source = buttonText ?? [];
  if (!Array.isArray(source) || !source.length) return ["", ""];
  return [String(source[0] ?? ""), String(source[1] ?? "")];
};

const resolveEntityRef = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value.key || value._id || value.id || "";
};

export const formatRallyDate = (dateValue) => {
  if (!dateValue) return "-";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString();
};

export const formatCategoryCc = (category) => {
  const min = category?.minCC;
  const max = category?.maxCC;
  if (min == null && max == null) return "-";
  if (max == null || max === "") return `${min ?? 0}+`;
  return `${min ?? 0} - ${max}`;
};

export const getRallyCategories = (rally) => {
  if (Array.isArray(rally?.categories) && rally.categories.length) {
    return rally.categories.filter(Boolean);
  }

  const details = rally?.categories_details || rally?.category_details;
  if (Array.isArray(details) && details.length) {
    return details.filter(Boolean);
  }
  if (details && typeof details === "object") return [details];

  if (rally?.category) return [rally.category];
  return [];
};

export const getRallyCategoryLabel = (category) => {
  if (!category) return "";
  if (typeof category === "string") return category;
  return category.title || category.key || category._id || category.id || "";
};

export const getRallyCategoryLabels = (rally) =>
  getRallyCategories(rally).map(getRallyCategoryLabel).filter(Boolean);

export const getCategorySelectValue = (category) => {
  if (!category) return "";
  if (typeof category === "string") return category;
  return category.key || category._id || category.id || "";
};

export const getRallyCategoryValue = (rally) =>
  getRallyCategories(rally).map(getCategorySelectValue).filter(Boolean);

export const getRallyRouteId = (rally) => {
  const route = rally?.route_id ?? rally?.routeId ?? rally?.route;
  if (!route) return "";
  if (typeof route === "string") return route;
  return route._id || route.id || "";
};

export const getTypeSelectValue = (type) => {
  if (!type) return "";
  if (typeof type === "string") return type;
  return type._id || type.id || type.key || "";
};

export const getRallyTypeValues = (rally) => {
  const raw = Array.isArray(rally?.types)
    ? rally.types
    : rally?.type
      ? [rally.type]
      : [];

  return raw.map(getTypeSelectValue).filter(Boolean);
};

export const getCategoryTypeTokens = (category) => {
  const type = category?.typeId || category?.type;
  if (!type) return [];
  if (typeof type === "string") return [type];
  return [type._id, type.id, type.key].filter(Boolean).map(String);
};

export const filterCategoriesBySelectedTypes = (
  categories = [],
  selectedTypes = []
) => {
  const selected = new Set(
    (selectedTypes || []).filter(Boolean).map((item) => String(item))
  );
  if (!selected.size) return [];

  return (categories || []).filter((category) =>
    getCategoryTypeTokens(category).some((token) => selected.has(String(token)))
  );
};

export const pruneCategorySelectionForTypes = (
  form,
  allCategories = [],
  selectedTypes = []
) => {
  const visible = filterCategoriesBySelectedTypes(allCategories, selectedTypes);
  const visibleKeys = new Set();

  visible.forEach((category) => {
    [
      getCategorySelectValue(category),
      category?._id,
      category?.id,
      category?.key,
    ]
      .filter(Boolean)
      .forEach((token) => visibleKeys.add(String(token)));
  });

  const nextCategories = (form.categories || []).filter((item) =>
    visibleKeys.has(String(item))
  );

  const nextPricing = {};
  nextCategories.forEach((categoryId) => {
    const amount = form.pricing?.[categoryId];
    if (amount != null && amount !== "") {
      nextPricing[categoryId] = amount;
    }
  });

  return {
    categories: nextCategories,
    pricing: nextPricing,
  };
};

export const getRallyApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  const message = data?.message || error?.message || fallback;

  if (Array.isArray(data?.errors) && data.errors.length) {
    return `${message}: ${data.errors.join(", ")}`;
  }

  if (typeof data?.error === "string" && data.error) {
    return data.error;
  }

  return message;
};

export const mapRallyToForm = (rally) => {
  const categories = getRallyCategoryValue(rally);
  const rawPricing = getRallyPricingMap(rally);
  const pricing = {};

  categories.forEach((categoryValue) => {
    if (rawPricing[categoryValue] != null && rawPricing[categoryValue] !== "") {
      pricing[categoryValue] = rawPricing[categoryValue];
      return;
    }

    const match = getRallyCategories(rally).find((category) => {
      if (!category || typeof category !== "object") {
        return String(category) === String(categoryValue);
      }
      const aliases = [
        getCategorySelectValue(category),
        category._id,
        category.id,
        category.key,
      ].filter(Boolean);
      return aliases.map(String).includes(String(categoryValue));
    });

    if (!match || typeof match !== "object") return;

    const aliases = [
      getCategorySelectValue(match),
      match._id,
      match.id,
      match.key,
    ].filter(Boolean);

    for (const alias of aliases) {
      if (rawPricing[alias] != null && rawPricing[alias] !== "") {
        pricing[categoryValue] = rawPricing[alias];
        break;
      }
    }
  });

  return {
    name: rally?.name || "",
    edition_number:
      rally?.edition_number === 0 || rally?.edition_number
        ? String(rally.edition_number)
        : "",
    route_id: getRallyRouteId(rally),
    types: getRallyTypeValues(rally),
    categories,
    pricing,
    date: toDateInput(rally?.date || rally?.rally_start_date),
    end_date: toDateInput(rally?.end_date),
    registration_start_date: toDateInput(rally?.registration_start_date),
    registration_end_date: toDateInput(rally?.registration_end_date),
    location: rally?.location || "",
    organiser: rally?.organiser || "",
    description: rally?.description || "",
    status: rally?.status || "upcoming",
    highlights:
      Array.isArray(rally?.highlights) && rally.highlights.length
        ? rally.highlights.map((item) => String(item ?? ""))
        : [""],
    stats_drivers: String(rally?.stats_drivers ?? 0),
    stats_events: String(rally?.stats_events ?? 0),
    stats_participants: String(rally?.stats_participants ?? 0),
    coverImage: String(rally?.coverImage ?? rally?.cover_image ?? ""),
    buttonText: mapButtonTextToForm(rally?.buttonText ?? rally?.button_text),
    imagesGallery: asStringArray(rally?.imagesGallery ?? rally?.images_gallery),
    videoGallery: asStringArray(rally?.videoGallery ?? rally?.video_gallery),
    showContentAsGlobal: Boolean(
      rally?.showContentAsGlobal ?? rally?.show_content_as_global
    ),
    documents: mapDocumentsToForm(rally?.documents),
    destinations: mapDestinationsToForm(rally?.destinations),
    track: mapTrackToForm(rally?.track),
  };
};

export const validateRallyForm = (form) => {
  const errors = {};

  if (!form.name?.trim()) errors.name = "Name is required.";
  if (!form.date) errors.date = "Rally start date is required.";
  if (!form.location?.trim()) errors.location = "Location is required.";
  if (!form.route_id) errors.route_id = "Route is required.";
  if (!Array.isArray(form.types) || form.types.length === 0) {
    errors.types = "Select at least one type.";
  }

  if (form.edition_number !== "" && Number(form.edition_number) < 0) {
    errors.edition_number = "Enter a valid edition number.";
  }

  ["stats_drivers", "stats_events", "stats_participants"].forEach((key) => {
    if (form[key] !== "" && Number(form[key]) < 0) {
      errors[key] = "Enter a valid number.";
    }
  });

  const buttonSlots = Array.isArray(form.buttonText) ? form.buttonText : [];
  const buttonA = String(buttonSlots[0] || "").trim();
  const buttonB = String(buttonSlots[1] || "").trim();
  if (Boolean(buttonA) !== Boolean(buttonB)) {
    errors.buttonText =
      "Provide exactly two button labels, or leave both empty.";
  }

  const pricingErrors = {};
  (form.categories || []).forEach((categoryId) => {
    const rawAmount = form.pricing?.[categoryId];
    if (rawAmount === "" || rawAmount == null) {
      pricingErrors[categoryId] = "Amount is required.";
      return;
    }
    const amount = Number(rawAmount);
    if (Number.isNaN(amount) || amount < 0) {
      pricingErrors[categoryId] = "Enter a valid non-negative amount.";
    }
  });
  if (Object.keys(pricingErrors).length) {
    errors.pricing = pricingErrors;
  }

  const documentErrors = {};
  (form.documents || []).forEach((doc, index) => {
    const url = String(doc?.documentUrl || "").trim();
    const title = String(doc?.title || "").trim();
    const bgImage = String(doc?.bgImage || "").trim();
    const hasAny = Boolean(url || title || bgImage);
    if (!hasAny) return;
    if (!url || !title) {
      documentErrors[index] = "Each document needs an uploaded file and title.";
    }
  });
  if (Object.keys(documentErrors).length) {
    errors.documents = documentErrors;
  }

  const destinationErrors = {};
  (form.destinations || []).forEach((item, index) => {
    const title = String(item?.title || "").trim();
    const image = String(item?.image || "").trim();
    const hasAny = Boolean(title || image);
    if (!hasAny) return;
    if (!title || !image) {
      destinationErrors[index] =
        "Each destination needs a title and image URL.";
    }
  });
  if (Object.keys(destinationErrors).length) {
    errors.destinations = destinationErrors;
  }

  return errors;
};

export const getRallyPricingMap = (rally) => {
  const map = {};

  const entries =
    rally?.pricing || rally?.prices || rally?.category_pricing || [];

  if (Array.isArray(entries)) {
    entries.forEach((item) => {
      const categoryId =
        resolveEntityRef(item?.category) ||
        resolveEntityRef(item?.category_id) ||
        resolveEntityRef(item?.categoryId) ||
        item?.category_key ||
        item?.categoryKey ||
        item?.key;
      const amount = item?.amount ?? item?.price;
      if (categoryId != null && amount != null && amount !== "") {
        map[categoryId] = String(amount);
      }
    });
  } else if (entries && typeof entries === "object") {
    Object.entries(entries).forEach(([key, value]) => {
      const amount =
        value && typeof value === "object"
          ? value.amount ?? value.price
          : value;
      if (amount != null && amount !== "") map[key] = String(amount);
    });
  }

  getRallyCategories(rally).forEach((category) => {
    if (!category || typeof category !== "object") return;
    const categoryId = getCategorySelectValue(category);
    const amount = category.amount ?? category.price ?? category.pricing;
    if (
      categoryId &&
      amount != null &&
      amount !== "" &&
      map[categoryId] == null
    ) {
      map[categoryId] = String(amount);
    }
  });

  return map;
};

export const getRallyEventId = (data) => {
  if (!data) return "";
  if (typeof data === "string") return data;
  const rally = normalizeRally(data);
  return rally?._id || rally?.id || "";
};

export const buildRallyPricesMap = (form) => {
  const prices = {};

  (form.categories || []).filter(Boolean).forEach((categoryKey) => {
    const amount = Number(form.pricing?.[categoryKey]);
    if (Number.isFinite(amount) && amount >= 0) {
      prices[categoryKey] = amount;
    }
  });

  return prices;
};

export const buildRallyPricesArray = (form) => {
  return (form.categories || [])
    .filter(Boolean)
    .map((category_id) => {
      const amount = Number(form.pricing?.[category_id]);
      if (!Number.isFinite(amount) || amount < 0) return null;
      return { category_id, amount };
    })
    .filter(Boolean);
};

/** @deprecated Prefer buildRallyPricesArray — prices are sent on create/update. */
export const buildRallyPricingPayloads = (form) => buildRallyPricesArray(form);

const buildDocumentsPayload = (documents = []) =>
  documents
    .map((doc) => {
      const documentUrl = String(doc?.documentUrl || "").trim();
      const title = String(doc?.title || "").trim();
      if (!documentUrl || !title) return null;

      const item = { documentUrl, title };
      const bgImage = String(doc?.bgImage || "").trim();
      if (bgImage) item.bgImage = bgImage;
      return item;
    })
    .filter(Boolean);

const buildDestinationsPayload = (destinations = []) =>
  destinations
    .map((item) => {
      const title = String(item?.title || "").trim();
      const image = String(item?.image || "").trim();
      if (!title || !image) return null;
      return { title, image };
    })
    .filter(Boolean);

const buildTrackPayload = (track) => {
  if (!track || typeof track !== "object") return null;
  const title = String(track.title || "").trim();
  const description = String(track.description || "").trim();
  const image = String(track.image || "").trim();
  if (!title && !description && !image) return null;

  const payload = {};
  if (title) payload.title = title;
  if (description) payload.description = description;
  if (image) payload.image = image;
  return payload;
};

const buildUrlList = (values = []) =>
  values.map((item) => String(item || "").trim()).filter(Boolean);

export const buildRallyPayload = (form, options = {}) => {
  const { isEdit = false } = options;

  const payload = {
    name: form.name.trim(),
    date: form.date,
    location: form.location.trim(),
    status: form.status || "upcoming",
  };

  if (form.end_date) payload.end_date = form.end_date;
  if (form.registration_start_date) {
    payload.registration_start_date = form.registration_start_date;
  }
  if (form.registration_end_date) {
    payload.registration_end_date = form.registration_end_date;
  }
  if (form.organiser?.trim()) payload.organiser = form.organiser.trim();
  if (form.description?.trim()) payload.description = form.description.trim();

  payload.route_id = form.route_id || null;
  payload.types = (Array.isArray(form.types) ? form.types : []).filter(Boolean);
  payload.categories = (
    Array.isArray(form.categories) ? form.categories : []
  ).filter(Boolean);

  if (form.edition_number !== "") {
    payload.edition_number = Number(form.edition_number);
  }

  const highlights = (form.highlights || [])
    .map((item) => String(item || "").trim())
    .filter(Boolean);
  if (highlights.length) payload.highlights = highlights;
  else if (isEdit) payload.highlights = [];

  payload.stats_drivers = Number(form.stats_drivers || 0);
  payload.stats_events = Number(form.stats_events || 0);
  payload.stats_participants = Number(form.stats_participants || 0);

  const prices = buildRallyPricesArray(form);
  if (prices.length) payload.prices = prices;

  const coverImage = String(form.coverImage || "").trim();
  if (coverImage) payload.coverImage = coverImage;
  else if (isEdit) payload.coverImage = "";

  const buttonSlots = Array.isArray(form.buttonText) ? form.buttonText : [];
  const buttonA = String(buttonSlots[0] || "").trim();
  const buttonB = String(buttonSlots[1] || "").trim();
  if (buttonA && buttonB) {
    payload.buttonText = [buttonA, buttonB];
  } else if (isEdit) {
    payload.buttonText = [];
  }

  const imagesGallery = buildUrlList(form.imagesGallery);
  if (imagesGallery.length) payload.imagesGallery = imagesGallery;
  else if (isEdit) payload.imagesGallery = [];

  const videoGallery = buildUrlList(form.videoGallery);
  if (videoGallery.length) payload.videoGallery = videoGallery;
  else if (isEdit) payload.videoGallery = [];

  if (!isEdit) {
    payload.showContentAsGlobal = Boolean(form.showContentAsGlobal);
  }

  const documents = buildDocumentsPayload(form.documents);
  if (documents.length) payload.documents = documents;
  else if (isEdit) payload.documents = [];

  const destinations = buildDestinationsPayload(form.destinations);
  if (destinations.length) payload.destinations = destinations;
  else if (isEdit) payload.destinations = [];

  const track = buildTrackPayload(form.track);
  if (track) payload.track = track;
  else if (isEdit) payload.track = {};

  return payload;
};

export const formatRallyDateRange = (startDate, endDate) => {
  const start = formatRallyDate(startDate);
  const end = formatRallyDate(endDate);
  if (start === "-" && end === "-") return "-";
  if (end === "-") return start;
  if (start === "-") return end;
  return `${start} – ${end}`;
};

export const getRallyCategoryTitle = (rally) => {
  const labels = getRallyCategoryLabels(rally);
  return labels.length ? labels.join(", ") : "-";
};

export const formatRallyStatus = (status) => {
  if (!status) return "-";
  return status.charAt(0).toUpperCase() + status.slice(1);
};

export const normalizeRallies = (data) => {
  if (Array.isArray(data)) return data;
  return data?.data || data?.rallies || data?.items || [];
};

export const normalizeRally = (data) => {
  if (!data) return null;
  if (Array.isArray(data)) return data[0] || null;
  if (data._id || data.id || data.name) return data;
  return data.data || data.rally || data.event || null;
};

export const filterRallies = (rallies, searchTerm) => {
  const term = searchTerm.trim().toLowerCase();
  if (!term) return rallies;

  return rallies.filter((rally) => {
    const name = rally?.name?.toLowerCase() || "";
    const location = rally?.location?.toLowerCase() || "";
    const organiser = rally?.organiser?.toLowerCase() || "";
    const status = rally?.status?.toLowerCase() || "";
    const categories = getRallyCategoryTitle(rally).toLowerCase();
    const edition = String(rally?.edition_number ?? "");

    return (
      name.includes(term) ||
      location.includes(term) ||
      organiser.includes(term) ||
      status.includes(term) ||
      categories.includes(term) ||
      edition.includes(term)
    );
  });
};
